import { ReqParams } from "@/types";
import axiosInstance from "./axiosInstance";

export type RouteTicketToGroupPayload = {
  groupId: number;
  note?: string;
};

export type GroupAssignTicketPayload = {
  memberAssignedTo: number;
  note?: string;
  selfNote?: string;
};

export type SelfAssignTicketPayload = {
  note?: string;
};

export type ResolveToL1Payload = {
  note?: string;
  resolutionNotes?: string;
  closureReason?: string;
};

export type ReassignGroupTicketPayload = {
  groupId: number;
  note?: string;
};

export type TransferTicketOwnerPayload = GroupAssignTicketPayload;

export default class TicketService {
  static async getAllList(params: ReqParams = {}) {
    const response = await axiosInstance.get("/agent/tickets", { params });
    return response.data;
  }

  static async getAssignedList(params: ReqParams = {}) {
    const response = await axiosInstance.get("/agent/assigned/tickets", { params });
    return response.data;
  }

  static async getDetails(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/agent/tickets/${params?.id}`, { params });
    return response.data;
  }

  static async getDetailsForChat(params: ReqParams = {}) {
    const response = await axiosInstance.get(`/customer/ticket/${params?.ticketId}/${params?.chatbotId}`, { params });
    return response.data;
  }

  static async assign(params: ReqParams = {}) {
    const response = await axiosInstance.post(`/agent/ticket/assign`, params, {
      skipGlobalLoader: true,
    });
    return response.data;
  }

  static async routeToGroup(
    ticketId: number,
    payload: RouteTicketToGroupPayload,
  ) {
    const response = await axiosInstance.post(
      `/tenant/tickets/${ticketId}/route-to-group`,
      payload,
      { skipGlobalLoader: true },
    );
    return response.data;
  }

  // Group manager (or System Admin) hands a queued group ticket to one of the
  // group's members.
  static async groupAssign(
    ticketId: number,
    payload: GroupAssignTicketPayload,
  ) {
    const response = await axiosInstance.post(
      `/tenant/tickets/${ticketId}/group-assign`,
      payload,
      { skipGlobalLoader: true },
    );
    return response.data;
  }

  // Group member takes an unassigned ticket out of the group queue.
  static async selfAssign(
    ticketId: number,
    payload: SelfAssignTicketPayload = {},
  ) {
    const response = await axiosInstance.post(
      `/tenant/tickets/${ticketId}/self-assign`,
      payload,
      { skipGlobalLoader: true },
    );
    return response.data;
  }

  // Group closes its part of the work and sends the ticket back to L1.
  static async resolveToL1(ticketId: number, payload: ResolveToL1Payload = {}) {
    const response = await axiosInstance.post(
      `/tenant/tickets/${ticketId}/resolve-to-l1`,
      payload,
      { skipGlobalLoader: true },
    );
    return response.data;
  }

  // The current group owner moves the ticket into another group queue.
  static async reassignGroup(
    ticketId: number,
    payload: ReassignGroupTicketPayload,
  ) {
    const response = await axiosInstance.post(
      `/tenant/tickets/${ticketId}/reassign-group`,
      payload,
      { skipGlobalLoader: true },
    );
    return response.data;
  }

  // A Group Manager or System Admin transfers ownership to another member of
  // the ticket's current group.
  static async transferOwner(
    ticketId: number,
    payload: TransferTicketOwnerPayload,
  ) {
    const response = await axiosInstance.post(
      `/tenant/tickets/${ticketId}/transfer-owner`,
      payload,
      { skipGlobalLoader: true },
    );
    return response.data;
  }

  static async sendMailToCustomer(params: ReqParams | FormData = {}) {
    const id = params instanceof FormData ? params.get('id') : (params as ReqParams)?.id;
    const response = await axiosInstance.post(`/agent/ticket/status-update/email/${id}`, params, {
      skipGlobalLoader: true,
    });
    return response.data;
  }

  // Reply to a social-channel ticket (facebook / instagram / whatsappchat).
  // Body is multipart FormData: { message, attachments? } (attachments may repeat).
  static async replyToTicket(ticketId: string | number, formData: FormData) {
    const response = await axiosInstance.post(`/agent/tickets/${ticketId}/reply`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      skipGlobalLoader: true,
    });
    return response.data;
  }

  static async updateStatus(params: ReqParams = {}) {
    const response = await axiosInstance.put(`/agent/ticket/status-update/${params?.id}`, params, {
      skipGlobalLoader: true,
    });
    return response.data;
  }

  static async updatePriority(params: ReqParams = {}) {
    const response = await axiosInstance.patch(`/agent/ticket/${params?.id}/priority`, params, {
      skipGlobalLoader: true,
    });
    return response.data;
  }

  static async updateCategory(params: ReqParams = {}) {
    const response = await axiosInstance.put(`/agent/ticket/category-update/${params?.id}`, params, {
      skipGlobalLoader: true,
    });
    return response.data;
  }

  static async addByChat(params: ReqParams = {}) {
    const response = await axiosInstance.post(`/chatbot`, { ...params, source: 'chatbot' });
    return response.data;
  }

  static async addMannually(params: ReqParams = {}) {
    const response = await axiosInstance.post(`/agent/tickets`, params);
    return response.data;
  }

  static async getTicketCategoryDropdown() {
    const response = await axiosInstance.get("tenant/ticket-category-dropdown");
    return response.data;
  }

  static async getTicketSubCategoryDropdown(categoryId: number | string) {
    const response = await axiosInstance.get(`tenant/ticket-sub-category-dropdown/${categoryId}`);
    return response.data;
  }

  // Sub-sub-category dropdown: cascades from a chosen category + sub-category.
  // Path params: /{categoryId}/{subCategoryId}
  static async getTicketSubSubCategoryDropdown(categoryId: number | string, subCategoryId: number | string) {
    const response = await axiosInstance.get(`tenant/ticket-sub-sub-category-dropdown/${categoryId}/${subCategoryId}`);
    return response.data;
  }
}
